import type { MessageRead } from '../../client'
import {
  humanToolName,
  messageBlocks,
  messageText,
  messageThinking,
  messageToolUses,
} from '../../lib/messageContent'
import type { ReasoningPart } from './ReasoningBlock'

export type Segment = { kind: 'reasoning'; parts: ReasoningPart[] } | { kind: 'answer'; text: string }

/** Reasoning and answer in the order the model produced them, so a reloaded message reads the
 * same way it did while streaming. Consecutive reasoning collapses into one box. */
export function segments(message: MessageRead, subMessages: MessageRead[]): Segment[] {
  const subsByTool = new Map<string, MessageRead[]>()
  for (const sub of subMessages) {
    const parent = sub.parent_tool_use_id
    if (parent) subsByTool.set(parent, [...(subsByTool.get(parent) ?? []), sub])
  }

  const out: Segment[] = []
  let run: ReasoningPart[] = []

  function flush() {
    if (run.length > 0) out.push({ kind: 'reasoning', parts: run })
    run = []
  }

  for (const block of messageBlocks(message.content)) {
    if (block.type === 'thinking') {
      const thinking = typeof block.thinking === 'string' ? block.thinking.trim() : ''
      if (thinking) run.push({ kind: 'text', text: thinking })
      continue
    }

    if (block.type === 'tool_use') {
      const id = String(block.id ?? '')
      run.push({
        kind: 'tool',
        toolUseId: id,
        label:
          (typeof block.display_name === 'string' && block.display_name) ||
          humanToolName(String(block.name ?? 'tool')),
        running: false,
      })

      for (const sub of subsByTool.get(id) ?? []) {
        const subThinking = messageThinking(sub.content)
        if (subThinking) run.push({ kind: 'text', text: subThinking })

        for (const subTool of messageToolUses(sub.content)) {
          run.push({
            kind: 'tool',
            toolUseId: subTool.id,
            label: subTool.displayName || humanToolName(subTool.name),
            running: false,
          })
        }

        const subText = messageText(sub.content)
        if (subText) run.push({ kind: 'text', text: subText })
      }
      continue
    }

    if (block.type === 'text') {
      const text = typeof block.text === 'string' ? block.text.trim() : ''
      if (text) {
        flush()
        out.push({ kind: 'answer', text })
      }
    }
  }

  flush()
  return out
}
